import { useMemo, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FieldError } from 'react-hook-form';
import { addMonths, format, parseISO } from 'date-fns';

import { CloseIconSVG } from 'src/assets/svg';
import Form from 'src/components/FormComponents/Form';
import Input, {
    Error as InputError,
    Label as InputLabel,
} from 'src/components/FormComponents/Input';
import CheckBox from 'src/components/FormComponents/CheckBox';
import { EventAttendeeGroupsSelect } from './EventAttendeeGroupsSelect';
import { AppModal } from 'src/components/Modals/AppModal';
import { Button } from 'src/components/Buttons/Button';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import {
    EventRecurrence,
    EventRecurrenceFrequency,
    EventStatus,
    EventType,
    getEventTypeStoredColor,
    getMunduEventType,
    MunduEvent,
} from 'src/domain/event';
import { OrganizationRolePermission } from 'src/domain/organization';
import { isOrganizationAdministrator, UserRole } from 'src/domain/user';
import { getPrimaryRole } from 'src/domain/user';
import { useCurrentOrganization } from 'src/view/hooks/useCurrentOrganization';
import { useCreatorRoleContext } from 'src/view/hooks/useCreatorRoleContext';
import { useCreatorRolePermission } from 'src/view/hooks/useCreatorRolePermission';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import {
    excludeRoomGroupsFromAttendees,
    getTopSelectedEventGroupIds,
    hasIndividualRoomSelection,
} from 'src/utils/eventAttendeeGroups';
import { getHttpErrorMessage } from 'src/utils/httpErrorMessage';
import { EventOrganizer } from './EventOrganizer';
import { EventAudienceSections } from './EventAudienceSections';
import { EventReadOnlyModal } from './EventReadOnlyModal';
import { EventIconPicker, EventTypePicker } from './EventFormPickers';
import { RecurringEventScopeChoice } from './RecurringEventScopeChoice';
import {
    EventFormValues,
    EventReadOnlyReason,
    getBrowserTimeZone,
    getEndDateAfterStartChange,
    getOriginalOccurrenceStart,
    getRecurrenceLabelDate,
    NO_EVENT_ICON,
    NO_RECURRENCE,
    replaceEventInputDate,
    responseDeadlineIsFuture,
    toEventFormInputValue,
} from './eventFormModel';

const {
    cradle: { munduClient },
} = container;

const requireResponseRolloutEnabled = () =>
    process.env.REACT_APP_RSVP_AUTHORING_ENABLED === 'true';

const EventDateInput = ({
    control,
    name,
    allDay,
    label,
    error,
    fallbackTime,
    disabled,
    onValueChange,
}: {
    control: Control<EventFormValues>;
    name: 'startDate' | 'endDate';
    allDay?: boolean;
    label: string;
    error?: FieldError;
    fallbackTime: string;
    disabled?: boolean;
    onValueChange?: (previousValue: string, nextValue: string) => void;
}) => (
    <Controller
        control={control}
        name={name}
        render={({ field }) => (
            <Input
                {...field}
                className="text-input"
                type={allDay ? 'date' : 'datetime-local'}
                value={allDay ? field.value.slice(0, 10) : field.value}
                onChange={event => {
                    const nextValue = allDay
                        ? replaceEventInputDate(
                              field.value,
                              event.currentTarget.value,
                              fallbackTime,
                          )
                        : event.currentTarget.value;
                    field.onChange(nextValue);
                    onValueChange?.(field.value, nextValue);
                }}
                label={label}
                error={error}
                disabled={disabled}
            />
        )}
    />
);

export type EventFormModalProps = {
    show: boolean;
    event?: MunduEvent;
    initialStart?: Date;
    initialEnd?: Date;
    onClose: () => void;
    onSaved: () => void;
    onDeleted?: () => void;
    readOnly?: boolean;
    readOnlyReason?: EventReadOnlyReason;
};

type PendingCloseAction = 'closeModal' | 'cancelEditing';
type RecurringEditScope = 'occurrence' | 'series';

export const EventFormModal = ({
    show,
    event,
    initialStart,
    initialEnd,
    onClose,
    onSaved,
    onDeleted,
    readOnly = false,
    readOnlyReason = 'ownership',
}: EventFormModalProps) => {
    const { t, i18n } = useTranslation();
    const notify = useNotification();
    const user = useSignedInUser();
    const role = getPrimaryRole(user);
    const { activeRoleGroupIds } = useCreatorRoleContext();
    const canReadResidents = useCreatorRolePermission(
        OrganizationRolePermission.readResidents,
    );
    const { organizationId, organization } = useCurrentOrganization();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
        useState(false);
    const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] =
        useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [recurringEditScope, setRecurringEditScope] =
        useState<RecurringEditScope>();
    const [seriesEvent, setSeriesEvent] = useState<MunduEvent>();
    const [isLoadingSeries, setIsLoadingSeries] = useState(false);
    const [pendingCloseAction, setPendingCloseAction] =
        useState<PendingCloseAction>();
    const [pendingScheduleValues, setPendingScheduleValues] =
        useState<EventFormValues>();
    const [isSavingSchedule, setIsSavingSchedule] = useState(false);
    const getFormValuesRef = useRef<() => EventFormValues>();

    const isEdit = Boolean(event);
    const isCancelled = event?.status === EventStatus.cancelled;
    const isReadOnly = isEdit && (readOnly || isCancelled);
    const isViewingDetails =
        isEdit &&
        (!isEditing || isReadOnly) &&
        !isDeleteConfirmationOpen &&
        !isCancelConfirmationOpen;
    const isOccurrenceEdit =
        Boolean(event?.recurrence) && recurringEditScope === 'occurrence';
    const isChoosingRecurringEditScope =
        Boolean(event?.recurrence) && isEditing && !recurringEditScope;
    const formEvent =
        recurringEditScope === 'series' && seriesEvent ? seriesEvent : event;
    const isOrganizationAdmin = isOrganizationAdministrator(user);
    const canSendMessagesToAll = useCreatorRolePermission(
        OrganizationRolePermission.sendMessagesToAll,
        { scope: 'any' },
    );
    const canSendToAll =
        isOrganizationAdmin ||
        (role === UserRole.creator && canSendMessagesToAll);
    const organizationName =
        organization?.organizationDisplayName?.trim() ||
        organization?.organizationName?.trim();
    const sendToAllLabel = organizationName
        ? t('modules.calendar.form.sendToAllLabelWithOrg', {
              organizationName,
          })
        : t('modules.calendar.form.sendToAllLabel');
    const readOnlyAttendeeLabels = event?.target?.sendToAll
        ? [sendToAllLabel]
        : (event?.target?.groupIds ?? [])
              .map(groupId =>
                  organization?.groups?.find(group => group.id === groupId),
              )
              .filter((group): group is { id: string; name: string } =>
                  Boolean(group),
              )
              .map(group => group.name);

    const allowedGroupIds = useMemo(
        () =>
            role === UserRole.creator && !isOrganizationAdmin
                ? canReadResidents
                    ? activeRoleGroupIds
                    : []
                : undefined,
        [activeRoleGroupIds, canReadResidents, isOrganizationAdmin, role],
    );

    const allowedGroupIdSet = useMemo(
        () =>
            allowedGroupIds === undefined
                ? undefined
                : new Set(allowedGroupIds),
        [allowedGroupIds],
    );

    const groupOptions = useMemo(() => {
        if (!organization?.groups?.length) {
            return [];
        }

        if (role !== UserRole.creator || isOrganizationAdmin || isReadOnly) {
            return organization.groups;
        }

        if (!allowedGroupIds?.length) {
            return [];
        }

        return organization.groups.filter(group =>
            allowedGroupIdSet?.has(group.id),
        );
    }, [
        organization?.groups,
        role,
        isOrganizationAdmin,
        isReadOnly,
        allowedGroupIds,
        allowedGroupIdSet,
    ]);

    const selectableGroupIdSet = useMemo(
        () => new Set(groupOptions.map(group => group.id)),
        [groupOptions],
    );

    const defaultValues = useMemo<EventFormValues>(() => {
        if (formEvent) {
            return {
                title: formEvent.title,
                description: formEvent.description ?? '',
                location: formEvent.location ?? '',
                startDate: toEventFormInputValue(formEvent.startDate),
                endDate: toEventFormInputValue(formEvent.endDate),
                allDay: formEvent.allDay ?? false,
                requireResponse: formEvent.requireResponse ?? false,
                type: getMunduEventType(formEvent),
                icon: formEvent.icon ?? NO_EVENT_ICON,
                recurrenceFrequency:
                    formEvent.recurrence?.frequency ?? NO_RECURRENCE,
                recurrenceUntil: formEvent.recurrence
                    ? format(parseISO(formEvent.recurrence.until), 'yyyy-MM-dd')
                    : '',
                sendToAll: formEvent.target?.sendToAll ?? false,
                groupIds: isReadOnly
                    ? formEvent.target?.groupIds ?? []
                    : formEvent.target?.groupIds?.filter(groupId =>
                          selectableGroupIdSet.has(groupId),
                      ) ?? [],
            };
        }

        const start = initialStart ?? new Date();
        const end = initialEnd ?? new Date(start.getTime() + 60 * 60 * 1000);

        return {
            title: '',
            description: '',
            location: '',
            startDate: format(start, "yyyy-MM-dd'T'HH:mm"),
            endDate: format(end, "yyyy-MM-dd'T'HH:mm"),
            allDay: false,
            requireResponse: false,
            type: isOrganizationAdmin ? EventType.residents : EventType.group,
            icon: NO_EVENT_ICON,
            recurrenceFrequency: NO_RECURRENCE,
            recurrenceUntil: format(addMonths(start, 3), 'yyyy-MM-dd'),
            sendToAll: false,
            groupIds: [],
        };
    }, [
        formEvent,
        initialStart,
        initialEnd,
        isReadOnly,
        selectableGroupIdSet,
        isOrganizationAdmin,
    ]);

    const handleSubmit = async (
        values: EventFormValues,
        scheduleConfirmed = false,
    ) => {
        if (!organizationId || isReadOnly) return;

        const disablingResponse =
            requireResponseRolloutEnabled() &&
            Boolean(formEvent?.requireResponse) &&
            !values.requireResponse &&
            !isOccurrenceEdit;
        if (
            disablingResponse &&
            !window.confirm(t('modules.calendar.form.disableResponseConfirm'))
        )
            return;

        const sendToAll =
            canSendToAll && values.type !== EventType.group
                ? Boolean(values.sendToAll)
                : false;
        const accessibleGroupIds = values.groupIds?.filter(
            groupId =>
                selectableGroupIdSet.has(groupId) &&
                (allowedGroupIdSet ? allowedGroupIdSet.has(groupId) : true),
        );
        if (
            values.type === EventType.residents &&
            !sendToAll &&
            hasIndividualRoomSelection(
                accessibleGroupIds ?? [],
                organization?.groups ?? [],
            )
        ) {
            notify(t('modules.calendar.form.removeRoomsToUseOpen'), true);
            return;
        }
        const groupIds = accessibleGroupIds?.length
            ? getTopSelectedEventGroupIds(
                  accessibleGroupIds,
                  organization?.groups ?? [],
              )
            : undefined;

        if (
            !isOccurrenceEdit &&
            !sendToAll &&
            !groupIds &&
            !formEvent?.target?.consumerIds?.length
        ) {
            notify(
                t(
                    values.type === EventType.group
                        ? 'modules.calendar.form.groupAudienceHint'
                        : 'modules.calendar.form.targetsRequired',
                ),
                true,
            );
            return;
        }

        const start = new Date(values.startDate);
        const end = new Date(values.endDate);
        const isRecurring = values.recurrenceFrequency !== NO_RECURRENCE;

        if (end < start) {
            notify(t('modules.calendar.form.endBeforeStart'), true);
            return;
        }

        let seriesStart = start;
        let seriesEnd = end;

        if (
            !isOccurrenceEdit &&
            isRecurring &&
            formEvent?.recurrence &&
            formEvent.seriesStartDate &&
            formEvent.seriesEndDate
        ) {
            const occurrenceStart = parseISO(formEvent.startDate);
            const occurrenceEnd = parseISO(formEvent.endDate);
            seriesStart = new Date(
                parseISO(formEvent.seriesStartDate).getTime() +
                    (start.getTime() - occurrenceStart.getTime()),
            );
            seriesEnd = new Date(
                parseISO(formEvent.seriesEndDate).getTime() +
                    (end.getTime() - occurrenceEnd.getTime()),
            );
        }

        let recurrence: EventRecurrence | undefined;
        if (isRecurring && !isOccurrenceEdit) {
            if (!values.recurrenceUntil) {
                notify(
                    t('modules.calendar.form.recurrenceUntilRequired'),
                    true,
                );
                return;
            }

            const recurrenceUntil = new Date(
                `${values.recurrenceUntil}T23:59:59.999`,
            );
            if (recurrenceUntil < seriesStart) {
                notify(
                    t('modules.calendar.form.recurrenceUntilBeforeStart'),
                    true,
                );
                return;
            }

            recurrence = {
                frequency:
                    values.recurrenceFrequency as EventRecurrenceFrequency,
                interval: formEvent?.recurrence?.interval ?? 1,
                until: recurrenceUntil.toISOString(),
                timeZone: formEvent
                    ? formEvent.recurrence?.timeZone ??
                      formEvent.timeZone ??
                      'Europe/Oslo'
                    : getBrowserTimeZone(),
            };
        }

        const changingRsvpSchedule = Boolean(
            event &&
                (formEvent?.hasRsvpHistory ?? formEvent?.requireResponse) &&
                !isOccurrenceEdit &&
                (seriesStart.getTime() !==
                    parseISO(
                        formEvent.seriesStartDate ?? formEvent.startDate,
                    ).getTime() ||
                    !isEqual(recurrence, formEvent.recurrence)),
        );
        if (changingRsvpSchedule && !scheduleConfirmed) {
            setPendingScheduleValues(values);
            return;
        }

        const payload = {
            title: values.title.trim(),
            // Empty strings intentionally clear values on an existing event.
            // The API already normalizes them to undefined when creating one.
            description: values.description?.trim() ?? '',
            location: values.location?.trim() ?? '',
            startDate: seriesStart.toISOString(),
            endDate: seriesEnd.toISOString(),
            allDay: Boolean(values.allDay),
            type: values.type,
            internal: values.type === EventType.internal,
            color: getEventTypeStoredColor(values.type),
            sendToAll,
            groupIds,
            consumerIds: formEvent?.target?.consumerIds,
            ...(requireResponseRolloutEnabled() &&
            values.type === EventType.relatives &&
            values.requireResponse &&
            !formEvent?.requireResponse
                ? { requireResponse: true }
                : {}),
            ...(disablingResponse
                ? { requireResponse: false, confirmDisableResponse: true }
                : {}),
        };
        const icon = values.icon || undefined;

        try {
            if (event) {
                if (isOccurrenceEdit) {
                    const originalOccurrenceStart =
                        getOriginalOccurrenceStart(event);

                    if (!originalOccurrenceStart) {
                        throw new Error(
                            'Recurring event occurrence is missing its original start date',
                        );
                    }

                    await munduClient.updateEventOccurrence(
                        event.id,
                        originalOccurrenceStart,
                        {
                            title: payload.title,
                            description: payload.description,
                            location: payload.location,
                            startDate: start.toISOString(),
                            endDate: end.toISOString(),
                            allDay: payload.allDay,
                            icon: icon ?? (event.icon ? null : undefined),
                        },
                    );
                } else {
                    await munduClient.updateEvent(event.id, {
                        ...payload,
                        ...(changingRsvpSchedule
                            ? { confirmRsvpScheduleChange: true }
                            : {}),
                        icon: icon ?? (formEvent?.icon ? null : undefined),
                        recurrence:
                            recurrence ??
                            (formEvent?.recurrence ? null : undefined),
                    });
                }
                notify(t('modules.calendar.notifications.updated'));
            } else {
                await munduClient.createEvent({
                    ...payload,
                    icon,
                    organizationId,
                    recurrence,
                    timeZone: getBrowserTimeZone(),
                });
                notify(t('modules.calendar.notifications.created'));
            }
            setIsEditing(false);
            setRecurringEditScope(undefined);
            setSeriesEvent(undefined);
            setPendingCloseAction(undefined);
            getFormValuesRef.current = undefined;
            onSaved();
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        }
    };

    const handleDelete = async (scope: RecurringEditScope = 'series') => {
        if (!event || readOnly) return;
        setIsDeleting(true);

        try {
            if (scope === 'occurrence' && event.recurrence) {
                const originalOccurrenceStart =
                    getOriginalOccurrenceStart(event);

                if (!originalOccurrenceStart) {
                    throw new Error(
                        'Recurring event occurrence is missing its original start date',
                    );
                }

                await munduClient.deleteEventOccurrence(
                    event.id,
                    originalOccurrenceStart,
                );
            } else {
                await munduClient.deleteEvent(event.id);
            }
            notify(t('modules.calendar.notifications.deleted'));
            setIsDeleteConfirmationOpen(false);
            setIsEditing(false);
            setRecurringEditScope(undefined);
            setSeriesEvent(undefined);
            setPendingCloseAction(undefined);
            getFormValuesRef.current = undefined;
            onDeleted?.();
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleCancelEvent = async (scope: RecurringEditScope = 'series') => {
        if (!event || readOnly || isCancelled) return;
        setIsCancelling(true);

        try {
            if (scope === 'occurrence' && event.recurrence) {
                const originalOccurrenceStart =
                    getOriginalOccurrenceStart(event);
                if (!originalOccurrenceStart) {
                    throw new Error(
                        'Recurring event occurrence is missing its original start date',
                    );
                }
                await munduClient.cancelEventOccurrence(
                    event.id,
                    originalOccurrenceStart,
                );
            } else {
                await munduClient.cancelEvent(event.id);
            }
            notify(t('modules.calendar.notifications.cancelled'));
            setIsCancelConfirmationOpen(false);
            setIsEditing(false);
            setRecurringEditScope(undefined);
            setSeriesEvent(undefined);
            getFormValuesRef.current = undefined;
            onSaved();
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleRestoreEvent = async () => {
        if (!event || readOnly || !isCancelled) return;
        setIsRestoring(true);

        try {
            if (event.recurrence && event.isOccurrenceCancelled) {
                const originalOccurrenceStart =
                    getOriginalOccurrenceStart(event);
                if (!originalOccurrenceStart) {
                    throw new Error(
                        'Recurring event occurrence is missing its original start date',
                    );
                }
                await munduClient.restoreEventOccurrence(
                    event.id,
                    originalOccurrenceStart,
                );
            } else {
                await munduClient.restoreEvent(event.id);
            }
            notify(t('modules.calendar.notifications.restored'));
            onSaved();
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        } finally {
            setIsRestoring(false);
        }
    };

    const completeCloseAction = (action: PendingCloseAction) => {
        setIsDeleteConfirmationOpen(false);
        setIsCancelConfirmationOpen(false);
        setPendingCloseAction(undefined);
        getFormValuesRef.current = undefined;

        if (action === 'cancelEditing' && isEdit && isEditing) {
            setIsEditing(false);
            setRecurringEditScope(undefined);
            setSeriesEvent(undefined);
            return;
        }

        setIsEditing(false);
        setRecurringEditScope(undefined);
        setSeriesEvent(undefined);
        onClose();
    };

    const requestClose = (action: PendingCloseAction) => {
        if (isDeleting || isCancelling || isRestoring) return;

        const currentValues = getFormValuesRef.current?.();
        if (currentValues && !isEqual(currentValues, defaultValues)) {
            setIsDeleteConfirmationOpen(false);
            setIsCancelConfirmationOpen(false);
            setPendingCloseAction(action);
            return;
        }

        completeCloseAction(action);
    };

    const closeModal = () => {
        if (pendingScheduleValues) {
            if (!isSavingSchedule) setPendingScheduleValues(undefined);
            return;
        }
        if (pendingCloseAction) {
            setPendingCloseAction(undefined);
            return;
        }

        requestClose('closeModal');
    };

    const cancelEditing = () => requestClose('cancelEditing');

    const chooseRecurringEditScope = async (scope: RecurringEditScope) => {
        if (scope === 'occurrence' || !event) {
            setRecurringEditScope(scope);
            return;
        }

        setIsLoadingSeries(true);
        try {
            const storedSeries = await munduClient.getEvent(event.id);
            setSeriesEvent(storedSeries);
            setRecurringEditScope('series');
        } catch (error) {
            const message = await getHttpErrorMessage(
                error,
                t('admin.notifications.genericError'),
            );
            notify(message, true);
        } finally {
            setIsLoadingSeries(false);
        }
    };

    if (!show) return null;

    if (isViewingDetails && event) {
        return (
            <EventReadOnlyModal
                show={show}
                event={event}
                reason={readOnlyReason}
                attendeeLabels={readOnlyAttendeeLabels}
                canEdit={!isReadOnly}
                canDelete={Boolean(isCancelled && !readOnly)}
                canRestore={Boolean(isCancelled && !readOnly)}
                isRestoring={isRestoring}
                onClose={closeModal}
                onEdit={() => {
                    setRecurringEditScope(undefined);
                    setSeriesEvent(undefined);
                    setIsEditing(true);
                }}
                onDelete={() => setIsDeleteConfirmationOpen(true)}
                onRestore={handleRestoreEvent}
            />
        );
    }

    return (
        <div className="fixed z-100 inset-0 overflow-y-auto bg-black bg-opacity-75 transition-opacity">
            <AppModal show={show} clickOutside={closeModal}>
                <div
                    className={`absolute top-1/2 left-1/2 flex max-h-[92vh] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-30px bg-lightPeach text-woodyBrown shadow-xl ${
                        isDeleteConfirmationOpen ||
                        isCancelConfirmationOpen ||
                        pendingScheduleValues ||
                        pendingCloseAction ||
                        isChoosingRecurringEditScope
                            ? 'w-[min(94vw,720px)]'
                            : 'w-[min(94vw,1120px)]'
                    }`}
                >
                    <header className="flex flex-shrink-0 items-center justify-between gap-4 border-b border-woodyBrown/15 px-5 py-5 sm:px-8">
                        <h2 className="text-xl font-semibold">
                            {pendingCloseAction
                                ? t('modules.calendar.form.discardConfirmTitle')
                                : pendingScheduleValues
                                ? t(
                                      'modules.calendar.form.rsvpScheduleChangeTitle',
                                  )
                                : isChoosingRecurringEditScope
                                ? t('modules.calendar.form.editRecurringTitle')
                                : isDeleteConfirmationOpen
                                ? t(
                                      event?.recurrence
                                          ? 'modules.calendar.form.deleteSeriesConfirmTitle'
                                          : 'modules.calendar.form.deleteConfirmTitle',
                                  )
                                : isCancelConfirmationOpen
                                ? t(
                                      event?.recurrence
                                          ? 'modules.calendar.form.cancelSeriesConfirmTitle'
                                          : 'modules.calendar.form.cancelConfirmTitle',
                                  )
                                : isEdit
                                ? t(
                                      isReadOnly
                                          ? 'modules.calendar.form.viewTitle'
                                          : 'modules.calendar.form.editTitle',
                                  )
                                : t('modules.calendar.form.createTitle')}
                        </h2>
                        <button
                            type="button"
                            aria-label={t('common.close')}
                            title={t('common.close')}
                            onClick={closeModal}
                            disabled={
                                isDeleting ||
                                isCancelling ||
                                isRestoring ||
                                isSavingSchedule
                            }
                            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-woodyBrown transition-colors hover:bg-woodyBrown hover:text-lightPeach focus:outline-none disabled:pointer-events-none disabled:opacity-40"
                        >
                            <CloseIconSVG className="h-3 w-3" />
                        </button>
                    </header>
                    <Form<EventFormValues>
                        key={recurringEditScope ?? 'event'}
                        schema={EventFormValues}
                        onSubmit={values => handleSubmit(values)}
                        mode="onChange"
                        defaultValues={defaultValues}
                        className="flex min-h-0 flex-1 flex-col overflow-hidden"
                    >
                        {methods => {
                            getFormValuesRef.current = methods.getValues;

                            const {
                                register,
                                control,
                                watch,
                                setValue,
                                formState: { errors, isSubmitting },
                            } = methods;

                            const sendToAll =
                                (canSendToAll || isReadOnly) &&
                                watch('type') !== EventType.group
                                    ? watch('sendToAll')
                                    : false;
                            const recurrenceFrequency = watch(
                                'recurrenceFrequency',
                            );
                            const allDay = watch('allDay');
                            const eventType = watch('type');
                            const selectedGroupIds = watch('groupIds') ?? [];
                            const individualRoomsSelected =
                                hasIndividualRoomSelection(
                                    selectedGroupIds,
                                    organization?.groups ?? [],
                                );
                            const wholeAudienceOptions =
                                excludeRoomGroupsFromAttendees(
                                    organization?.groups ?? [],
                                ).filter(
                                    group =>
                                        sendToAll ||
                                        selectableGroupIdSet.has(group.id),
                                );
                            // Keep stored selections visible even when a type
                            // does not offer rooms as new audience choices.
                            const displayedGroupOptions =
                                eventType === EventType.group
                                    ? groupOptions
                                    : [
                                          ...wholeAudienceOptions,
                                          ...groupOptions.filter(
                                              group =>
                                                  selectedGroupIds.includes(
                                                      group.id,
                                                  ) &&
                                                  !wholeAudienceOptions.some(
                                                      option =>
                                                          option.id ===
                                                          group.id,
                                                  ),
                                          ),
                                      ];
                            const internal = eventType === EventType.internal;
                            const handleTypeChange = (type: EventType) => {
                                if (type === EventType.group) {
                                    setValue('sendToAll', false, {
                                        shouldDirty: true,
                                    });
                                }
                                if (type === EventType.residents) {
                                    setValue(
                                        'groupIds',
                                        getTopSelectedEventGroupIds(
                                            selectedGroupIds,
                                            organization?.groups ?? [],
                                        ),
                                        { shouldDirty: true },
                                    );
                                }
                            };
                            const handleEveryoneChange = (checked: boolean) => {
                                setValue('sendToAll', checked, {
                                    shouldDirty: true,
                                });
                            };
                            const handleStartDateChange = (
                                previousStart: string,
                                nextStart: string,
                            ) => {
                                const nextEnd = getEndDateAfterStartChange(
                                    previousStart,
                                    nextStart,
                                    methods.getValues('endDate'),
                                );
                                if (nextEnd) {
                                    setValue('endDate', nextEnd, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }
                            };
                            const startDate = watch('startDate');
                            const recurrenceLabelDate = getRecurrenceLabelDate(
                                startDate,
                                event,
                            );
                            const recurrenceWeekday =
                                recurrenceLabelDate?.toLocaleDateString(
                                    i18n.language,
                                    { weekday: 'long' },
                                );
                            const recurrenceDayOfMonth =
                                recurrenceLabelDate?.getDate();
                            const isRecurring =
                                recurrenceFrequency !== NO_RECURRENCE;
                            const hasFutureResponseDeadline =
                                isRecurring ||
                                responseDeadlineIsFuture(
                                    startDate,
                                    Boolean(allDay),
                                    formEvent?.timeZone ?? getBrowserTimeZone(),
                                );
                            const deleteConfirmationKey = event?.internal
                                ? event.recurrence
                                    ? 'modules.calendar.form.deleteInternalSeriesConfirm'
                                    : 'modules.calendar.form.deleteInternalConfirm'
                                : event?.recurrence
                                ? 'modules.calendar.form.deleteSeriesConfirm'
                                : 'modules.calendar.form.deleteConfirm';

                            if (pendingScheduleValues) {
                                return (
                                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                        <div className="flex flex-1 items-center justify-center overflow-y-auto px-5 py-8 sm:px-8 sm:py-10">
                                            <div className="w-full max-w-lg rounded-2xl bg-white bg-opacity-50 p-5 sm:p-6">
                                                <p>
                                                    {t(
                                                        'modules.calendar.form.rsvpScheduleChangeConfirm',
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <footer className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 sm:px-8">
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.keepEditing',
                                                )}
                                                size="comfortable"
                                                onClick={() =>
                                                    setPendingScheduleValues(
                                                        undefined,
                                                    )
                                                }
                                                disabled={isSavingSchedule}
                                            />
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.rsvpScheduleChangeProceed',
                                                )}
                                                size="comfortable"
                                                onClick={async () => {
                                                    setIsSavingSchedule(true);
                                                    try {
                                                        await handleSubmit(
                                                            pendingScheduleValues,
                                                            true,
                                                        );
                                                    } finally {
                                                        setIsSavingSchedule(
                                                            false,
                                                        );
                                                        setPendingScheduleValues(
                                                            undefined,
                                                        );
                                                    }
                                                }}
                                                disabled={isSavingSchedule}
                                                active
                                            />
                                        </footer>
                                    </div>
                                );
                            }

                            if (pendingCloseAction) {
                                return (
                                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                        <div className="flex flex-1 items-center justify-center overflow-y-auto px-5 py-8 sm:px-8 sm:py-10">
                                            <div className="w-full max-w-lg rounded-2xl bg-white bg-opacity-50 p-5 sm:p-6">
                                                <p>
                                                    {t(
                                                        'modules.calendar.form.discardConfirm',
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <footer className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 sm:px-8">
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.keepEditing',
                                                )}
                                                size="comfortable"
                                                onClick={() =>
                                                    setPendingCloseAction(
                                                        undefined,
                                                    )
                                                }
                                            />
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.discardChanges',
                                                )}
                                                size="comfortable"
                                                onClick={() =>
                                                    completeCloseAction(
                                                        pendingCloseAction,
                                                    )
                                                }
                                                active
                                            />
                                        </footer>
                                    </div>
                                );
                            }

                            if (isChoosingRecurringEditScope) {
                                return (
                                    <RecurringEventScopeChoice
                                        action="edit"
                                        busy={isLoadingSeries}
                                        onCancel={cancelEditing}
                                        onOccurrence={() =>
                                            chooseRecurringEditScope(
                                                'occurrence',
                                            )
                                        }
                                        onSeries={() =>
                                            chooseRecurringEditScope('series')
                                        }
                                    />
                                );
                            }

                            if (isCancelConfirmationOpen) {
                                if (event?.recurrence) {
                                    return (
                                        <RecurringEventScopeChoice
                                            action="cancel"
                                            busy={isCancelling}
                                            onCancel={() =>
                                                setIsCancelConfirmationOpen(
                                                    false,
                                                )
                                            }
                                            onOccurrence={() =>
                                                handleCancelEvent('occurrence')
                                            }
                                            onSeries={() =>
                                                handleCancelEvent('series')
                                            }
                                        />
                                    );
                                }

                                return (
                                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                        <div className="flex flex-1 items-center justify-center overflow-y-auto px-5 py-8 sm:px-8 sm:py-10">
                                            <div className="w-full max-w-lg rounded-2xl bg-white bg-opacity-50 p-5 sm:p-6">
                                                <p>
                                                    {t(
                                                        'modules.calendar.form.cancelConfirm',
                                                        {
                                                            title: event?.title,
                                                        },
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <footer className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 sm:px-8">
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.keepEvent',
                                                )}
                                                size="comfortable"
                                                onClick={() =>
                                                    setIsCancelConfirmationOpen(
                                                        false,
                                                    )
                                                }
                                                disabled={isCancelling}
                                            />
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.cancelEvent',
                                                )}
                                                size="comfortable"
                                                onClick={() =>
                                                    handleCancelEvent()
                                                }
                                                disabled={isCancelling}
                                                active
                                            />
                                        </footer>
                                    </div>
                                );
                            }

                            if (isDeleteConfirmationOpen) {
                                if (event?.recurrence) {
                                    return (
                                        <RecurringEventScopeChoice
                                            action="delete"
                                            busy={isDeleting}
                                            onCancel={() =>
                                                setIsDeleteConfirmationOpen(
                                                    false,
                                                )
                                            }
                                            onOccurrence={() =>
                                                handleDelete('occurrence')
                                            }
                                            onSeries={() =>
                                                handleDelete('series')
                                            }
                                        />
                                    );
                                }

                                return (
                                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                                        <div className="flex flex-1 items-center justify-center overflow-y-auto px-5 py-8 sm:px-8 sm:py-10">
                                            <div className="w-full max-w-lg rounded-2xl bg-white bg-opacity-50 p-5 sm:p-6">
                                                <p>
                                                    {t(deleteConfirmationKey, {
                                                        title: event?.title,
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                        <footer className="flex flex-shrink-0 flex-wrap justify-end gap-3 border-t border-woodyBrown/15 bg-lightPeach px-5 py-4 sm:px-8">
                                            <Button
                                                title={t('common.cancel')}
                                                size="comfortable"
                                                onClick={() =>
                                                    setIsDeleteConfirmationOpen(
                                                        false,
                                                    )
                                                }
                                                disabled={isDeleting}
                                            />
                                            <Button
                                                title={t(
                                                    'modules.calendar.form.delete',
                                                )}
                                                size="comfortable"
                                                onClick={() => handleDelete()}
                                                disabled={isDeleting}
                                                active
                                            />
                                        </footer>
                                    </div>
                                );
                            }

                            return (
                                <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                                    <div className="flex-1 px-5 py-5 sm:px-8 sm:py-6">
                                        <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(360px,2fr)] lg:items-start">
                                            {isReadOnly && (
                                                <p className="rounded-2xl border border-woodyBrown/15 bg-white bg-opacity-50 px-4 py-3 text-sm lg:col-span-2">
                                                    {t(
                                                        readOnlyReason ===
                                                            'moduleMembership'
                                                            ? 'modules.calendar.form.moduleMembershipReadOnlyNotice'
                                                            : 'modules.calendar.form.readOnlyNotice',
                                                    )}
                                                </p>
                                            )}
                                            <section className="space-y-4 rounded-2xl bg-white bg-opacity-50 p-4 sm:p-5">
                                                <h3 className="font-semibold">
                                                    {t(
                                                        'modules.calendar.form.detailsSectionTitle',
                                                    )}
                                                </h3>
                                                <Input
                                                    className="text-input"
                                                    label={t(
                                                        'modules.calendar.form.titleLabel',
                                                    )}
                                                    error={errors.title}
                                                    readOnly={isReadOnly}
                                                    {...register('title')}
                                                />
                                                <div className="space-y-4 border-t border-woodyBrown/15 pt-4">
                                                    <h4 className="font-medium">
                                                        {t(
                                                            'modules.calendar.form.scheduleSectionTitle',
                                                        )}
                                                    </h4>
                                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                        <EventDateInput
                                                            control={control}
                                                            name="startDate"
                                                            allDay={allDay}
                                                            label={t(
                                                                'modules.calendar.form.startLabel',
                                                            )}
                                                            error={
                                                                errors.startDate
                                                            }
                                                            fallbackTime="00:00"
                                                            disabled={
                                                                isReadOnly
                                                            }
                                                            onValueChange={
                                                                handleStartDateChange
                                                            }
                                                        />
                                                        <EventDateInput
                                                            control={control}
                                                            name="endDate"
                                                            allDay={allDay}
                                                            label={t(
                                                                'modules.calendar.form.endLabel',
                                                            )}
                                                            error={
                                                                errors.endDate
                                                            }
                                                            fallbackTime="23:59"
                                                            disabled={
                                                                isReadOnly
                                                            }
                                                        />
                                                    </div>
                                                    <div className="flex items-center text-sm">
                                                        <CheckBox
                                                            label=""
                                                            aria-label={t(
                                                                'modules.calendar.form.allDayLabel',
                                                            )}
                                                            className="flex-shrink-0"
                                                            disabled={
                                                                isSubmitting ||
                                                                isReadOnly
                                                            }
                                                            {...register(
                                                                'allDay',
                                                            )}
                                                        />
                                                        <span
                                                            className={
                                                                isSubmitting ||
                                                                isReadOnly
                                                                    ? 'text-mongooseGray'
                                                                    : 'text-woodyBrown'
                                                            }
                                                        >
                                                            {t(
                                                                'modules.calendar.form.allDayLabel',
                                                            )}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <InputLabel
                                                            label={t(
                                                                'modules.calendar.form.recurrenceLabel',
                                                            )}
                                                        />
                                                        <select
                                                            className="text-input w-full"
                                                            disabled={
                                                                isReadOnly ||
                                                                isOccurrenceEdit
                                                            }
                                                            {...register(
                                                                'recurrenceFrequency',
                                                            )}
                                                        >
                                                            <option
                                                                value={
                                                                    NO_RECURRENCE
                                                                }
                                                            >
                                                                {t(
                                                                    'modules.calendar.form.doesNotRepeat',
                                                                )}
                                                            </option>
                                                            <option
                                                                value={
                                                                    EventRecurrenceFrequency.daily
                                                                }
                                                            >
                                                                {t(
                                                                    'modules.calendar.form.recurrenceDaily',
                                                                )}
                                                            </option>
                                                            <option
                                                                value={
                                                                    EventRecurrenceFrequency.weekly
                                                                }
                                                            >
                                                                {t(
                                                                    recurrenceWeekday
                                                                        ? 'modules.calendar.form.recurrenceWeeklyOn'
                                                                        : 'modules.calendar.form.recurrenceWeekly',
                                                                    {
                                                                        weekday:
                                                                            recurrenceWeekday,
                                                                    },
                                                                )}
                                                            </option>
                                                            <option
                                                                value={
                                                                    EventRecurrenceFrequency.monthly
                                                                }
                                                            >
                                                                {t(
                                                                    recurrenceDayOfMonth
                                                                        ? 'modules.calendar.form.recurrenceMonthlyOnDay'
                                                                        : 'modules.calendar.form.recurrenceMonthly',
                                                                    {
                                                                        day: recurrenceDayOfMonth,
                                                                    },
                                                                )}
                                                            </option>
                                                        </select>
                                                    </div>
                                                    {isRecurring && (
                                                        <Input
                                                            className="text-input"
                                                            type="date"
                                                            min={startDate?.slice(
                                                                0,
                                                                10,
                                                            )}
                                                            label={t(
                                                                'modules.calendar.form.recurrenceUntilLabel',
                                                            )}
                                                            error={
                                                                errors.recurrenceUntil
                                                            }
                                                            disabled={
                                                                isReadOnly ||
                                                                isOccurrenceEdit
                                                            }
                                                            {...register(
                                                                'recurrenceUntil',
                                                            )}
                                                        />
                                                    )}
                                                    {event?.recurrence && (
                                                        <div className="space-y-1 text-sm text-woodyBrown/70">
                                                            <p>
                                                                {t(
                                                                    isOccurrenceEdit
                                                                        ? 'modules.calendar.form.occurrenceEditNotice'
                                                                        : 'modules.calendar.form.seriesEditNotice',
                                                                )}
                                                            </p>
                                                            {!isOccurrenceEdit &&
                                                                event.hasOccurrenceExceptions && (
                                                                    <p>
                                                                        {t(
                                                                            'modules.calendar.form.seriesExceptionResetNotice',
                                                                        )}
                                                                    </p>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                                <Input
                                                    className="text-input"
                                                    label={t(
                                                        'modules.calendar.form.locationLabel',
                                                    )}
                                                    error={errors.location}
                                                    readOnly={isReadOnly}
                                                    {...register('location')}
                                                />
                                                <div>
                                                    <InputLabel
                                                        label={t(
                                                            'modules.calendar.form.descriptionLabel',
                                                        )}
                                                    />
                                                    <textarea
                                                        className="text-input min-h-[110px] w-full resize-y"
                                                        readOnly={isReadOnly}
                                                        {...register(
                                                            'description',
                                                        )}
                                                    />
                                                    <InputError
                                                        error={
                                                            errors.description as
                                                                | FieldError
                                                                | undefined
                                                        }
                                                    />
                                                </div>
                                                <div className="flex flex-wrap gap-4">
                                                    <EventTypePicker
                                                        control={control}
                                                        openTypeDisabledReason={
                                                            individualRoomsSelected
                                                                ? t(
                                                                      'modules.calendar.form.removeRoomsToUseOpen',
                                                                  )
                                                                : undefined
                                                        }
                                                        onTypeChange={
                                                            handleTypeChange
                                                        }
                                                        disabled={
                                                            isReadOnly ||
                                                            isOccurrenceEdit
                                                        }
                                                    />
                                                    <EventIconPicker
                                                        control={control}
                                                        disabled={isReadOnly}
                                                    />
                                                </div>
                                                {event && (
                                                    <EventOrganizer
                                                        label={t(
                                                            'modules.calendar.form.organizerLabel',
                                                        )}
                                                        name={
                                                            event.createdByName?.trim() ||
                                                            t(
                                                                'modules.calendar.form.organizerUnknown',
                                                            )
                                                        }
                                                        avatarUrl={
                                                            event.createdByAvatarUrl
                                                        }
                                                    />
                                                )}
                                            </section>

                                            <EventAudienceSections
                                                organizationId={
                                                    organizationId ?? ''
                                                }
                                                eventType={eventType}
                                                sendToAll={Boolean(sendToAll)}
                                                groupIds={selectedGroupIds}
                                                consumerIds={
                                                    formEvent?.target
                                                        ?.consumerIds ?? []
                                                }
                                                isOccurrenceEdit={
                                                    isOccurrenceEdit
                                                }
                                                occurrenceCounts={formEvent}
                                                attendees={
                                                    <>
                                                        {internal && (
                                                            <p className="rounded-xl bg-white bg-opacity-50 px-3 py-2 text-sm">
                                                                {t(
                                                                    'modules.calendar.form.types.internal.description',
                                                                )}
                                                            </p>
                                                        )}
                                                        {eventType ===
                                                            EventType.group && (
                                                            <p className="text-sm">
                                                                {t(
                                                                    'modules.calendar.form.groupAudienceHint',
                                                                )}
                                                            </p>
                                                        )}
                                                        {eventType !==
                                                            EventType.group &&
                                                            (canSendToAll ||
                                                                (isReadOnly &&
                                                                    event
                                                                        ?.target
                                                                        ?.sendToAll)) && (
                                                                <CheckBox
                                                                    label={
                                                                        sendToAllLabel
                                                                    }
                                                                    disabled={
                                                                        isSubmitting ||
                                                                        isReadOnly ||
                                                                        isOccurrenceEdit
                                                                    }
                                                                    {...register(
                                                                        'sendToAll',
                                                                    )}
                                                                    onChange={event =>
                                                                        handleEveryoneChange(
                                                                            event
                                                                                .target
                                                                                .checked,
                                                                        )
                                                                    }
                                                                />
                                                            )}
                                                        <EventAttendeeGroupsSelect
                                                            showResidentNames={
                                                                eventType ===
                                                                EventType.group
                                                            }
                                                            control={control}
                                                            name="groupIds"
                                                            organizationId={
                                                                organizationId
                                                            }
                                                            groups={
                                                                displayedGroupOptions
                                                            }
                                                            allSelected={
                                                                sendToAll
                                                            }
                                                            allowedGroupIds={
                                                                sendToAll ||
                                                                isReadOnly
                                                                    ? undefined
                                                                    : allowedGroupIds
                                                            }
                                                            error={
                                                                Array.isArray(
                                                                    errors.groupIds,
                                                                )
                                                                    ? (errors
                                                                          .groupIds[0] as FieldError)
                                                                    : (errors.groupIds as
                                                                          | FieldError
                                                                          | undefined)
                                                            }
                                                            disabled={
                                                                sendToAll ||
                                                                isReadOnly ||
                                                                isOccurrenceEdit
                                                            }
                                                        />
                                                    </>
                                                }
                                                replyOption={
                                                    requireResponseRolloutEnabled() &&
                                                    eventType ===
                                                        EventType.relatives &&
                                                    (!event ||
                                                        formEvent?.canConfigureRequireResponse ||
                                                        isOccurrenceEdit) && (
                                                        <div className="space-y-2">
                                                            <CheckBox
                                                                label={t(
                                                                    'modules.calendar.form.requireResponse',
                                                                )}
                                                                helperText={
                                                                    isOccurrenceEdit
                                                                        ? t(
                                                                              'modules.calendar.form.requireResponseSeriesOnly',
                                                                          )
                                                                        : !formEvent?.requireResponse &&
                                                                          !hasFutureResponseDeadline
                                                                        ? t(
                                                                              'modules.calendar.form.requireResponseFutureStart',
                                                                          )
                                                                        : undefined
                                                                }
                                                                disabled={
                                                                    isReadOnly ||
                                                                    isSubmitting ||
                                                                    isOccurrenceEdit ||
                                                                    (!formEvent?.requireResponse &&
                                                                        !hasFutureResponseDeadline)
                                                                }
                                                                {...register(
                                                                    'requireResponse',
                                                                )}
                                                            />
                                                        </div>
                                                    )
                                                }
                                            />
                                        </div>
                                    </div>

                                    <footer className="sticky bottom-0 z-10 flex flex-shrink-0 flex-nowrap items-center justify-between gap-2 border-t border-woodyBrown/15 bg-lightPeach px-4 py-4 sm:gap-3 sm:px-8">
                                        {isEdit && !isReadOnly ? (
                                            <div className="flex flex-nowrap gap-2 sm:gap-3">
                                                <Button
                                                    title={t(
                                                        'modules.calendar.form.delete',
                                                    )}
                                                    size="comfortable"
                                                    onClick={() =>
                                                        setIsDeleteConfirmationOpen(
                                                            true,
                                                        )
                                                    }
                                                    disabled={
                                                        isSubmitting ||
                                                        isDeleting ||
                                                        isCancelling
                                                    }
                                                />
                                                <Button
                                                    title={t(
                                                        'modules.calendar.form.cancelEvent',
                                                    )}
                                                    size="comfortable"
                                                    onClick={() =>
                                                        setIsCancelConfirmationOpen(
                                                            true,
                                                        )
                                                    }
                                                    disabled={
                                                        isSubmitting ||
                                                        isDeleting ||
                                                        isCancelling
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <span />
                                        )}
                                        <div className="ml-auto flex flex-nowrap justify-end gap-2 sm:gap-3">
                                            <Button
                                                title={t(
                                                    isReadOnly
                                                        ? 'common.close'
                                                        : 'common.cancel',
                                                )}
                                                size="comfortable"
                                                onClick={cancelEditing}
                                                disabled={
                                                    isSubmitting ||
                                                    isDeleting ||
                                                    isCancelling
                                                }
                                            />
                                            {!isReadOnly && (
                                                <Button
                                                    submit
                                                    size="comfortable"
                                                    title={
                                                        isEdit
                                                            ? t('common.save')
                                                            : t(
                                                                  'modules.calendar.form.submit',
                                                              )
                                                    }
                                                    disabled={
                                                        isSubmitting ||
                                                        isDeleting ||
                                                        isCancelling
                                                    }
                                                />
                                            )}
                                        </div>
                                    </footer>
                                </div>
                            );
                        }}
                    </Form>
                </div>
            </AppModal>
        </div>
    );
};
